<template>
  <div
    :class="chatStore.isFirstQuestionSent ? 'header-with-logotype' : 'header'"
    v-if="state"
    ref="headerRef"
  >
    <div class="header-left-container">
      <Icon
        v-if="chatStore.isFirstQuestionSent"
        :model="
          appStore.currentMode() === DisplayMode.Light
            ? appStore.configuration?.components.logotype.light
            : appStore.configuration?.components.logotype.dark
        "
        :alt="t('logotype.alt')"
        :key="componentKey"
        class="header-logotype"
      />
    </div>
    <div class="header-right-container">
      <BackToProduct :model="appStore.configuration?.components.header.backToProduct">
      </BackToProduct>

      <ButtonHelp
        :model="appStore.configuration?.components.header.buttonHelp"
        :alt="t('header.helpButton.alt')"
        :click="handleHelpButtonClick"
        :text="t('header.helpButton.text')"
        :isDisabled="false"
        :key="componentKey"
        :show="showHelpButton"
      >
      </ButtonHelp>

      <div class="desktop-nav">
        <LanguageSelector
          :model="appStore.configuration?.languages"
          :key="componentKey"
          :compact-display="compactDisplay"
        />

        <ThemeSwitcher :key="componentKey"> </ThemeSwitcher>

        <ButtonSimple
          :model="appStore.configuration?.components.header.buttonSimple"
          :alt="t('header.exportToPdfButton.alt')"
          :text="t('header.exportToPdfButton.text')"
          :click="handleExportToPdfButtonClick"
          :isDisabled="false"
          :key="componentKey"
          :show="showExportToPdfButton"
        >
        </ButtonSimple>

        <UserProfile @logout-click="onLogoutClick" />
      </div>
      <div class="mobile-nav">
        <HeaderHamburger :model="props.model" :header-height="headerHeight"></HeaderHamburger>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import { updateComponentKey } from '../composables/ComponentKey'
import { useAppStore } from '../stores/appStore'
import { useUserProfileStore } from '../stores/userProfileStore'
import { t } from '../composables/Translation'
import { useRouter } from 'vue-router'
import { State } from '../types/app/state'
import { LoginRequired } from '../types/app/loginRequired'
import LanguageSelector from './LanguageSelector.vue'
import UserProfile from './UserProfile.vue'
import ButtonHelp from './ButtonHelp.vue'
import ButtonSimple from './ButtonSimple.vue'
import type { Header } from '../types/configuration/Header'
import type { Language } from '../types/configuration/Language'
import Icon from './Icon.vue'
import { useChatStore } from '../stores/chatStore'
import { DisplayMode } from '../types/app/displayMode'
import ThemeSwitcher from './ThemeSwitcher.vue'
import HeaderHamburger from './HeaderHamburger.vue'
import BackToProduct from './BackToProduct.vue'

const router = useRouter()

const props = defineProps<{
  model: Header | undefined
  languages: Language[] | undefined
}>()

const appStore = useAppStore()
const chatStore = useChatStore()

const emit = defineEmits<{
  (e: 'open-help'): void
}>()

const pushedForMenu = ref(false)

const headerRef = ref<HTMLElement | null>(null)
const headerHeight = ref(56)

function updateHeaderHeight() {
  const el = headerRef.value
  if (!el) return
  headerHeight.value = Math.round(el.getBoundingClientRect().height)
}

let ro: ResizeObserver | null = null

let componentKey = updateComponentKey(props)
const state = computed(() => appStore.currentState)
const loginRequired = computed(() => appStore.configuration?.login.isLoginRequired)

const showExportToPdfButton = computed(() => state.value === State.Main)
const showHelpButton = computed(() => state.value === State.Main)

function onLogoutClick(): void {
  const userProfileStore = useUserProfileStore()
  userProfileStore.clearUserProfile()

  if (loginRequired.value === LoginRequired.SRRequiredOnly) {
    router.push({ name: 'main' })
  } else if (loginRequired.value === LoginRequired.Always) {
    router.push({ name: 'login' })
  }
}

function printConversation() {
  window.print()
}

function handleHelpButtonClick() {
  emit('open-help')
}

function handleExportToPdfButtonClick(): void {
  printConversation()
}

const compactDisplay = ref(false)
function updateCompactDisplay() {
  compactDisplay.value = window.outerWidth < 992
}

onMounted(() => {
  updateCompactDisplay()
  window.addEventListener('resize', updateCompactDisplay)

  nextTick(updateHeaderHeight)
  requestAnimationFrame(updateHeaderHeight)

  if ('ResizeObserver' in window && headerRef.value) {
    ro = new ResizeObserver(() => updateHeaderHeight())
    ro.observe(headerRef.value)
  }

  window.addEventListener('resize', updateHeaderHeight)

  try {
    document.fonts?.addEventListener?.('loadingdone', updateHeaderHeight)
  } catch {}
})

onUnmounted(() => {
  window.removeEventListener('resize', updateCompactDisplay)

  window.removeEventListener('resize', updateHeaderHeight)
  ro?.disconnect()

  if (pushedForMenu.value) {
    pushedForMenu.value = false
    history.back()
  }
})
</script>

<style scoped lang="scss">
.header,
.header-with-logotype {
  position: sticky;
  top: 0;
  background-color: var(--background-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 1002;
  padding: 16px;
}

:deep(.header-logotype .icon) {
  max-height: 40px;
}

.header-right-container {
  gap: 32px;
  margin-bottom: 3px;
}

.header-with-logotype {
  border-bottom: 1px solid var(--border-color);
}

.header-left-container,
.header-right-container {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
}

.desktop-nav {
  display: contents;
}
.mobile-nav {
  display: none;
}

@media (max-width: 992px) {
  .desktop-nav {
    display: none;
  }
  .mobile-nav {
    display: flex;
  }

  :deep(.header-logotype .icon) {
    max-height: 35px;
    max-width: 80px;
  }
}
</style>
